/**
 * @fileoverview Company name title-casing helper
 *
 * Purpose: Convert ABR-supplied company names from ALL CAPS
 * (e.g. "EXAMPLE COMPANY PTY LTD") into title case
 * (e.g. "Example Company Pty Ltd") for display in the create-job
 * dialog and for rendering in generated Day 1 documents.
 *
 * Lives in `convex/lib/` so it can be imported from both the Convex
 * backend (relative paths) and the React frontend (via the `@cvx`
 * alias) without duplicating logic.
 *
 * Used by:
 * - src/routes/_app/_auth/dashboard/-ui.create-job-dialog.tsx
 * - convex/jobs/precedents/fieldMapping.ts
 *
 * Linear: MOR-293
 */

/**
 * Tokens that should always render in upper case in AU company names.
 * Trust list — extend with care, every entry weakens the heuristic for
 * names that legitimately contain those letters as a normal word.
 */
const ALWAYS_UPPER = new Set<string>([
  "ATF", // "As Trustee For"
  "ACN",
  "ABN",
  "TFN",
  "ARBN",
  "ASIC",
  "GST",
  "NSW",
  "VIC",
  "QLD",
  "WA",
  "SA",
  "TAS",
  "NT",
  "ACT",
  "AU",
  "AUS",
  "USA",
  "UK",
  "NZ",
]);

/**
 * Lowercase mid-string articles / conjunctions / prepositions.
 * Always capitalised when they appear as the first word.
 */
const LOWER_MIDDLE = new Set<string>(["of", "and", "the", "or", "for", "in"]);

/** Roman numerals II–IX kept upper case. */
const ROMAN_NUMERALS = new Set<string>([
  "II",
  "III",
  "IV",
  "VI",
  "VII",
  "VIII",
  "IX",
]);

/**
 * Case a single contiguous run of letters (a "letter token") according
 * to the AU-specific allow-lists.
 *
 * @param token - A maximal run of A–Z / a–z (no separators inside)
 * @param isFirstWord - Whether this token is the very first letter run
 *   in the string (forces capitalisation of articles like "of"/"the").
 */
function caseLetterToken(token: string, isFirstWord: boolean): string {
  if (!token) return token;

  const upper = token.toUpperCase();
  if (ALWAYS_UPPER.has(upper)) return upper;
  if (ROMAN_NUMERALS.has(upper)) return upper;

  const lower = token.toLowerCase();
  if (!isFirstWord && LOWER_MIDDLE.has(lower)) return lower;

  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Convert an ALL CAPS company name into title case while preserving
 * common AU initialisms ("Pty Ltd", "ATF", "NSW", etc.).
 *
 * Returns the input unchanged if it already contains any lowercase
 * letter, so a manually-entered "Smith Pty Ltd" or a deliberately
 * cased "iiNet Limited" is never mangled.
 *
 * Internally we walk the string and treat each maximal run of
 * alphabetic characters as a separate "token" so that punctuation —
 * parentheses, ampersands, hyphens, apostrophes — does not hide
 * always-upper tokens like "(NSW)" from the allow-list.
 *
 * @param raw - Raw entity name as supplied by ABR or the user
 * @returns The title-cased company name
 *
 * @example
 *   titleCaseCompanyName("EXAMPLE COMPANY PTY LTD")
 *   // => "Example Company Pty Ltd"
 *
 *   titleCaseCompanyName("JOHN SMITH ATF SMITH FAMILY TRUST")
 *   // => "John Smith ATF Smith Family Trust"
 *
 *   titleCaseCompanyName("Example Company Pty Ltd")
 *   // => "Example Company Pty Ltd" (no-op)
 */
export function titleCaseCompanyName(raw: string): string {
  if (!raw) return raw;

  // If any lowercase letter exists, treat the input as already cased
  // and don't touch it. This avoids mangling user-entered names.
  if (/[a-z]/.test(raw)) return raw;

  type State = { out: string; buffer: string; firstWordSeen: boolean };

  const flush = (state: State): State => {
    if (!state.buffer) return state;
    return {
      out: state.out + caseLetterToken(state.buffer, !state.firstWordSeen),
      buffer: "",
      firstWordSeen: true,
    };
  };

  const final = Array.from(raw).reduce<State>(
    (state, ch) => {
      if (/[A-Za-z]/.test(ch)) {
        return { ...state, buffer: state.buffer + ch };
      }
      const flushed = flush(state);
      return { ...flushed, out: flushed.out + ch };
    },
    { out: "", buffer: "", firstWordSeen: false },
  );

  return flush(final).out;
}
